/**
 * Instant colour switch on the footwear PDP (template product.pdp-footwear).
 *
 * Every colour of a style is its own product. Tapping a colour swatch swaps the main-product
 * section for that product's in place, from a copy fetched ahead of time with the Section
 * Rendering API, instead of loading the page or waiting on the theme's own switch:
 *
 *  - Prefetch: copies of the other colours load while the page is idle (fewer on phones and
 *    slow connections, crossed-out colours last, nearest first) and when a swatch is hovered,
 *    focused or touched. The first gallery image is decoded ahead too. Copies live 5 minutes.
 *  - Swap: the section is replaced in one go, the address bar, title and meta follow, and the
 *    below-the-fold product sections (data-refresh on the script tag) are refreshed after.
 *  - The shopper's Size / Width carry over. When the new colour has the size, it is picked
 *    there, even if it is sold out in that colour (the box is crossed out and the button offers
 *    Notify Me). When the new colour doesn't make the size at all, it is remembered rather than
 *    dropped (assets/color-availability.js says so under the button) and picked again on the
 *    next colour that has it. When the copy was fetched for that exact size, the size arrives
 *    already picked and nothing else has to load.
 *  - A tap on a colour that isn't ready yet shows at once: the swatch ring and colour name move
 *    and the gallery dims until the swap lands (after 4 s the page simply navigates).
 *  - Back / forward between colours swap the same way.
 *
 * Measuring: window.__instantColorway.report() (and .stats) lists every swap with the time from
 * the tap to the new colour on screen; each swap is also a performance measure named ic:swap.
 */
(function () {
  "use strict";

  if (window.__instantColorway) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var script = document.currentScript;
  var REFRESH_KEYS = ((script && script.getAttribute("data-refresh")) || "")
    .split(",")
    .map(function (s) {
      return s.trim();
    })
    .filter(Boolean);
  var TTL_MS = 5 * 60 * 1000;
  var SWAP_WAIT_MS = 4000; // give up and navigate
  var HERO_WAIT_MS = 250; // hold the swap this long for the new first image to decode
  var VARIANT_WAIT_MS = 150; // hold it this long for a copy fetched for the picked size
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  var SWATCH = ".custom-meta-url[data-product-handle]";
  var OPTION_INPUT = "input.custom-option-buttons";
  var CROSSED = "is-color-unavailable";
  var TOUCH = !!(
    window.matchMedia &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches
  );
  var MAX_COPIES = TOUCH ? 5 : 10; // parsed section copies kept in memory, least recently used dropped

  var cache = new Map();
  var stats = [];
  var token = 0;
  var chosenOptions = {}; // fallback memory of the picks when assets/color-availability.js is absent
  var current = null;
  var pendingHandle = null;
  var pendingLabel = null;
  var idleRun = 0;
  var variantTimer = 0;
  var carrying = false; // change events fired by carryPicks, not by the shopper
  var belowRun = 0;
  var refocus = null; // { swatch, inputs, until }: see keepFocus()

  function noop() {}

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function norm(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase();
  }

  function mainSection() {
    return document.querySelector(".shopify-section.custom-main-product");
  }

  function sectionIdOf(el) {
    return el && el.id ? el.id.replace(/^shopify-section-/, "") : null;
  }

  function readData(scope) {
    var el = scope && scope.querySelector("script[data-instant-colorway]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function productPath(handle) {
    return ROOT + "products/" + encodeURIComponent(handle);
  }

  function idle(fn, timeout) {
    if ("requestIdleCallback" in window)
      return window.requestIdleCallback(fn, { timeout: timeout || 2000 });
    return setTimeout(fn, 150);
  }

  // How much to fetch while the page is idle. Hover / touch / focus prefetch is never limited:
  // the shopper is about to tap.
  function budget() {
    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || "")))
      return { pages: 0, heroes: 0, variants: 0 };
    var slow = !!c && c.effectiveType === "3g";
    if (TOUCH)
      return slow
        ? { pages: 2, heroes: 0, variants: 1 }
        : { pages: 4, heroes: 2, variants: 2 };
    return slow
      ? { pages: 4, heroes: 1, variants: 1 }
      : { pages: 8, heroes: 4, variants: 3 };
  }

  function entry(handle) {
    var e = cache.get(handle);
    if (!e) {
      e = {};
      cache.set(handle, e);
    }
    e.used = Date.now();
    return e;
  }

  // Drops stale copies, then the least recently used ones beyond MAX_COPIES. A copy being
  // swapped to right now is kept.
  function prune() {
    var now = Date.now();
    var live = [];
    cache.forEach(function (e, handle) {
      if (handle === pendingHandle) return;
      if (e.main && !fresh(e.main)) {
        cache.delete(handle);
        return;
      }
      if (e.wrapper) live.push({ handle: handle, used: e.used || 0 });
    });
    live.sort(function (a, b) {
      return a.used - b.used;
    });
    while (live.length > MAX_COPIES) cache.delete(live.shift().handle);
    return now;
  }

  function fresh(slot) {
    return !!slot && Date.now() - slot.at < TTL_MS;
  }

  function fetchText(url, priority) {
    var opts = { credentials: "same-origin" };
    if (priority) opts.priority = priority;
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.text();
    });
  }

  function parseSection(html, sid, handle) {
    var tpl = document.createElement("template");
    tpl.innerHTML = html;
    var wrapper =
      tpl.content.querySelector("#shopify-section-" + CSS.escape(sid)) ||
      tpl.content.firstElementChild;
    var data = readData(tpl.content);
    if (!wrapper || !data || data.handle !== handle)
      throw new Error("not swappable");
    return { tpl: tpl, wrapper: wrapper, data: data };
  }

  function getMain(handle, priority) {
    var e = entry(handle);
    if (fresh(e.main)) return e.main.p;
    var sid = sectionIdOf(mainSection());
    var p = fetchText(
      productPath(handle) + "?section_id=" + encodeURIComponent(sid),
      priority,
    ).then(function (html) {
      var parsed = parseSection(html, sid, handle);
      e.tpl = parsed.tpl;
      e.wrapper = parsed.wrapper;
      e.data = parsed.data;
      e.productJson = undefined;
      prune();
      return e;
    });
    e.main = { p: p, at: Date.now() };
    p.catch(function () {
      if (e.main && e.main.p === p) e.main = null;
    });
    return p;
  }

  /* ---------- the shopper's picks ---------- */

  // [{ name, value }] by option name, as the inputs carry them. assets/color-availability.js
  // knows the real picks (not the server's guesses) and the remembered ones; without it the
  // checked inputs are tracked here.
  function wantedNow() {
    var api = window.AetrexColorAvailability;
    if (api && typeof api.wanted === "function") {
      try {
        return api.wanted();
      } catch (err) {}
    }
    return Object.keys(chosenOptions).map(function (name) {
      return { name: name, value: chosenOptions[name] };
    });
  }

  function productJsonOf(e) {
    if (e.productJson === undefined) {
      var el =
        e.wrapper && e.wrapper.querySelector("script[data-product-json]");
      try {
        e.productJson = el ? JSON.parse(el.textContent) : null;
      } catch (err) {
        e.productJson = null;
      }
    }
    return e.productJson;
  }

  // The variant of that colour the picks name, or null when they don't name one (an option
  // with a choice left open, or a size this colour doesn't make). Options with a single value
  // in that colour don't need a pick.
  function variantIdFor(e, wanted) {
    var product = e && e.wrapper ? productJsonOf(e) : null;
    if (
      !product ||
      !product.variants ||
      !product.options ||
      !wanted ||
      !wanted.length
    )
      return null;
    var want = {};
    wanted.forEach(function (w) {
      want[norm(w.name)] = norm(w.value);
    });
    var names = product.options.map(function (o) {
      return norm(typeof o === "string" ? o : o.name);
    });
    var need = [];
    for (var i = 0; i < names.length; i++) {
      var values = {};
      product.variants.forEach(function (v) {
        values[norm(v.options[i])] = true;
      });
      if (Object.keys(values).length < 2) continue;
      if (want[names[i]] == null || !values[want[names[i]]]) return null;
      need.push(i);
    }
    if (!need.length) return null;
    var match = product.variants.find(function (v) {
      return need.every(function (i) {
        return norm(v.options[i]) === want[names[i]];
      });
    });
    return match ? match.id : null;
  }

  // A copy of the section rendered for that variant: the size arrives picked, with its price,
  // button and stock, so nothing has to re-render after the swap.
  function getVariant(handle, e, variantId, priority) {
    var slot = e.vmain;
    if (slot && slot.key === variantId && fresh(slot)) return slot.p;
    var sid = sectionIdOf(mainSection());
    var url =
      productPath(handle) +
      "?variant=" +
      encodeURIComponent(variantId) +
      "&section_id=" +
      encodeURIComponent(sid);
    slot = { key: variantId, at: Date.now(), done: null };
    slot.p = fetchText(url, priority).then(function (html) {
      var parsed = parseSection(html, sid, handle);
      parsed.variantId = variantId;
      slot.done = parsed;
      return parsed;
    });
    slot.p.catch(function () {
      if (e.vmain === slot) e.vmain = null;
    });
    e.vmain = slot;
    return slot.p;
  }

  // The variant copy if it is there (or lands within VARIANT_WAIT_MS), else null.
  function readyVariant(e, variantId) {
    var slot = e.vmain;
    if (!variantId || !slot || slot.key !== variantId || !fresh(slot))
      return Promise.resolve(null);
    if (slot.done) return Promise.resolve(slot.done);
    return Promise.race([
      slot.p.catch(function () {
        return null;
      }),
      delay(VARIANT_WAIT_MS).then(function () {
        return null;
      }),
    ]);
  }

  function prefetchVariant(handle, e, priority) {
    var id = variantIdFor(e, wantedNow());
    if (!id) return null;
    return getVariant(handle, e, id, priority);
  }

  /* ---------- below the fold ---------- */

  function belowIds() {
    var main = mainSection();
    if (!main || !REFRESH_KEYS.length) return [];
    var prefix = sectionIdOf(main).split("__")[0] + "__";
    var ids = [];
    document.querySelectorAll(".shopify-section").forEach(function (el) {
      var id = sectionIdOf(el);
      if (!id || id.indexOf(prefix) !== 0) return;
      var key = id.slice(prefix.length);
      if (
        REFRESH_KEYS.some(function (k) {
          return key.indexOf(k) === 0;
        })
      )
        ids.push(id);
    });
    return ids.slice(0, 5);
  }

  function getBelow(handle) {
    var e = entry(handle);
    if (fresh(e.below)) return e.below.p;
    var ids = belowIds();
    var p = !ids.length
      ? Promise.resolve({})
      : fetch(productPath(handle) + "?sections=" + ids.join(","), {
          credentials: "same-origin",
          priority: "low",
        }).then(function (r) {
          return r.ok ? r.json() : {};
        });
    e.below = { p: p, at: Date.now() };
    p.catch(function () {
      if (e.below && e.below.p === p) e.below = null;
    });
    return p;
  }

  /* ---------- images ---------- */

  function galleryImages(scope) {
    var mobile = window.matchMedia("(max-width: 767px)").matches;
    var list = null;
    scope.querySelectorAll(".product__media-list").forEach(function (ul) {
      if (!list && /\bmd:hidden\b/.test(ul.className) === mobile) list = ul;
    });
    var imgs = list
      ? Array.prototype.slice.call(list.querySelectorAll("img"))
      : [];
    if (!imgs.length) {
      var hero = scope.querySelector('img[fetchpriority="high"]');
      if (hero) imgs = [hero];
    }
    return { imgs: imgs, extra: mobile ? 1 : 3 };
  }

  function loadImage(src) {
    var img = new Image();
    img.decoding = "async";
    if (src.getAttribute("sizes")) img.sizes = src.getAttribute("sizes");
    var srcset =
      src.getAttribute("srcset") || src.getAttribute("data-lazy-srcset");
    if (srcset) img.srcset = srcset;
    img.src =
      src.getAttribute("src") || src.getAttribute("data-lazy-src") || "";
    return img;
  }

  function warmHero(e, more) {
    if (!e || !e.wrapper) return Promise.resolve();
    var g = galleryImages(e.wrapper);
    if (more && !e.warmedMore && g.imgs.length > 1) {
      e.warmedMore = true;
      g.imgs.slice(1, 1 + g.extra).forEach(loadImage);
    }
    if (e.warmed) return e.warmed;
    if (!g.imgs.length) {
      e.heroReady = true;
      return (e.warmed = Promise.resolve());
    }
    var img = loadImage(g.imgs[0]);
    e.warmed = (img.decode ? img.decode() : Promise.resolve())
      .then(function () {
        e.heroReady = true;
      })
      .catch(noop);
    return e.warmed;
  }

  /* ---------- prefetch ---------- */

  function prefetch(handle, priority) {
    if (!handle || (current && handle === current.handle)) return;
    var intent = priority === "high";
    getMain(handle, priority)
      .then(function (e) {
        if (intent) {
          var v = prefetchVariant(handle, e, "high");
          if (v)
            v.then(function (ve) {
              warmHero(ve, true);
            }).catch(noop);
        }
        return warmHero(e, intent);
      })
      .catch(noop);
  }

  // The other colours, nearest swatch first. Crossed-out ones (nothing in stock for the picked
  // size) are left to hover / touch prefetch.
  function siblings(skipCrossed) {
    var main = mainSection();
    if (!main) return [];
    var all = Array.prototype.slice.call(main.querySelectorAll(SWATCH));
    var shown = all.filter(function (el) {
      return el.getClientRects().length > 0;
    });
    var list = shown.length ? shown : all;
    var here = -1;
    list.forEach(function (el, i) {
      if (
        here === -1 &&
        current &&
        el.getAttribute("data-product-handle") === current.handle
      )
        here = i;
    });
    var seen = {};
    var out = [];
    list.forEach(function (el, i) {
      var h = el.getAttribute("data-product-handle");
      if (!h || seen[h] || (current && h === current.handle)) return;
      seen[h] = 1;
      if (skipCrossed && el.classList.contains(CROSSED)) return;
      out.push({ h: h, d: here === -1 ? i : Math.abs(i - here), i: i });
    });
    out.sort(function (a, b) {
      return a.d - b.d || a.i - b.i;
    });
    return out.map(function (x) {
      return x.h;
    });
  }

  function idlePrefetchAll() {
    var b = budget();
    if (!b.pages) return;
    var run = ++idleRun;
    var queue = siblings(true).slice(0, b.pages);
    var heroes = b.heroes;
    (function next() {
      if (run !== idleRun) return;
      var h = queue.shift();
      if (!h) {
        idlePrefetchVariants();
        return;
      }
      var warm = heroes > 0;
      heroes -= 1;
      idle(function () {
        if (run !== idleRun) return;
        getMain(h, "low")
          .then(function (e) {
            return warm ? Promise.race([warmHero(e), delay(1200)]) : null;
          })
          .catch(noop)
          .then(next);
      }, 4000);
    })();
  }

  // After a size pick: copies rendered for that size, for the nearest colours that have it.
  function idlePrefetchVariants() {
    var b = budget();
    if (!b.variants) return;
    var count = 0;
    siblings(true).some(function (h) {
      var e = cache.get(h);
      if (!e || !fresh(e.main) || !e.wrapper) return false;
      var v = prefetchVariant(h, e, "low");
      if (v) {
        v.catch(noop);
        count += 1;
      }
      return count >= b.variants;
    });
  }

  function queueVariantPrefetch() {
    clearTimeout(variantTimer);
    variantTimer = setTimeout(function () {
      idle(idlePrefetchVariants, 3000);
    }, 800);
  }

  /* ---------- swapping ---------- */

  function findInput(main, name, value) {
    var key = norm(value);
    var visible = null;
    var any = null;
    main.querySelectorAll(OPTION_INPUT).forEach(function (input) {
      if (input.name !== name || norm(input.value) !== key) return;
      if (!any) any = input;
      if (!visible && input.offsetParent !== null) visible = input;
    });
    return visible || any;
  }

  function selectionState() {
    var manager = window.addToCartButtonManager;
    return manager && manager.selection ? manager.selection : null;
  }

  // Re-picks the carried Size / Width on the colour just swapped in. A crossed-out (sold out in
  // this colour) value is still picked: the shopper chose that size. A value this colour
  // doesn't make is handed to assets/color-availability.js to remember. add-to-cart-button.js
  // has adopted the picks already (expect()); here the theme is told, with one change event
  // per option once every radio is set, so its section request carries every value. A copy
  // rendered for this variant still announces the pick so stock notification integrations update.
  function carryPicks(main, wanted, sized) {
    // returns the radios the theme was told about
    var api = window.AetrexColorAvailability;
    var selection = selectionState();
    var toAnnounce = [];

    (wanted || []).forEach(function (w) {
      var input = findInput(main, w.name, w.value);
      if (!input || input.disabled) {
        if (api && typeof api.setGhost === "function")
          api.setGhost(w.name, w.value, true);
        else delete chosenOptions[w.name];
        return;
      }
      if (api && typeof api.clearGhost === "function")
        api.clearGhost(w.name, true);
      input.checked = true;
      if (sized) {
        if (selection && typeof selection.record === "function")
          selection.record(input);
      }
      toAnnounce.push(input);
    });

    carrying = true;
    try {
      toAnnounce.forEach(function (input) {
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    } finally {
      carrying = false;
    }
    return toAnnounce;
  }

  // The theme focuses the radio of the last pick once its section request is back. After a
  // carried pick that would pull a keyboard shopper off the colour they just chose, so focus
  // goes back to the swatch, unless the shopper has moved on.
  function keepFocus(swatch, inputs) {
    refocus =
      swatch && inputs.length
        ? { swatch: swatch, inputs: inputs, until: Date.now() + 3000 }
        : null;
  }

  function onFocusIn(ev) {
    if (!refocus) return;
    if (Date.now() > refocus.until) {
      refocus = null;
      return;
    }
    if (refocus.inputs.indexOf(ev.target) === -1) {
      if (ev.target !== refocus.swatch) refocus = null; // the shopper moved on
      return;
    }
    var sw = refocus.swatch;
    refocus = null;
    if (sw.isConnected) sw.focus({ preventScroll: true });
  }

  function runScripts(container, knownSrcs) {
    container.querySelectorAll("script").forEach(function (old) {
      var type = (old.getAttribute("type") || "").trim().toLowerCase();
      if (
        type &&
        type !== "text/javascript" &&
        type !== "application/javascript" &&
        type !== "module"
      )
        return;
      if (
        !old.src &&
        window.AetrexTrueFit &&
        /truefitcorp\.com\/fitrec\//i.test(old.textContent) &&
        /t\('init'/.test(old.textContent)
      )
        return;
      var src = old.getAttribute("src");
      if (src) {
        var abs = new URL(src, location.href).href;
        if (knownSrcs[abs]) return;
        knownSrcs[abs] = true;
      } else if (
        window.__yotpoLazyLoader &&
        old.textContent.indexOf("__yotpoLazyLoader") !== -1
      ) {
        return;
      }
      var s = document.createElement("script");
      for (var i = 0; i < old.attributes.length; i++)
        s.setAttribute(old.attributes[i].name, old.attributes[i].value);
      if (!src) s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  function scriptSrcs() {
    var out = {};
    document.querySelectorAll("script[src]").forEach(function (s) {
      out[s.src] = true;
    });
    return out;
  }

  function replaceSection(target, wrapperFromTemplate) {
    var knownSrcs = scriptSrcs();
    // A clone, not importNode(): the theme's custom elements must be built once they are in the
    // page (ProductStickyForm looks its form up in its constructor), as with innerHTML.
    var incoming = wrapperFromTemplate.cloneNode(true);
    incoming.querySelectorAll("[data-animate]").forEach(function (n) {
      n.removeAttribute("data-animate");
    });
    incoming.querySelectorAll(".js-invisible").forEach(function (n) {
      n.classList.remove("js-invisible");
    });
    var kept = [];
    var hold = null;
    if (typeof Element.prototype.moveBefore === "function") {
      incoming
        .querySelectorAll("[data-ic-keep]")
        .forEach(function (placeholder) {
          var live = target.querySelector(
            '[data-ic-keep="' +
              CSS.escape(placeholder.getAttribute("data-ic-keep")) +
              '"]',
          );
          if (live) kept.push({ placeholder: placeholder, live: live });
        });
      if (kept.length) {
        hold = document.createElement("div");
        hold.hidden = true;
        document.body.appendChild(hold);
        kept.forEach(function (k) {
          hold.moveBefore(k.live, null);
        });
      }
    }
    target.replaceChildren.apply(
      target,
      Array.prototype.slice.call(incoming.childNodes),
    );
    kept.forEach(function (k) {
      var parent = k.placeholder.parentNode;
      if (parent && k.placeholder.isConnected) {
        parent.moveBefore(k.live, k.placeholder);
        k.placeholder.remove();
      }
    });
    if (hold) hold.remove();
    runScripts(target, knownSrcs);
  }

  function markActive(handle) {
    var main = mainSection();
    if (!main) return;
    main.querySelectorAll(SWATCH).forEach(function (el) {
      var on = el.getAttribute("data-product-handle") === handle;
      if (el.tagName === "A")
        el.setAttribute("aria-current", on ? "true" : "false");
      var label = el.querySelector(".custom-meta-label");
      if (label) label.classList.toggle("active-swatch", on);
    });
  }

  /* ---------- a tap on a colour that isn't ready yet ---------- */

  function injectStyles() {
    if (document.getElementById("instant-colorway-css")) return;
    var style = document.createElement("style");
    style.id = "instant-colorway-css";
    // The dim waits 120 ms, so a swap that lands at once never shows it.
    style.textContent =
      ".custom-main-product[data-ic-pending] media-gallery{opacity:.45;transition:opacity .2s ease .12s}" +
      ".custom-main-product[data-ic-pending]{cursor:progress}" +
      "@media (prefers-reduced-motion: reduce){.custom-main-product[data-ic-pending] media-gallery{transition:none}}";
    (document.head || document.documentElement).appendChild(style);
  }

  function colourLabels(main) {
    return Array.prototype.slice.call(
      main.querySelectorAll(".form__label--color .font-medium"),
    );
  }

  function showPending(main, handle) {
    if (!main) return;
    main.setAttribute("data-ic-pending", "");
    var sw = main.querySelector(
      SWATCH +
        '[data-product-handle="' +
        CSS.escape(handle) +
        '"][data-color-label]',
    );
    var name = sw ? sw.getAttribute("data-color-label") : "";
    if (!name) return;
    colourLabels(main).forEach(function (el) {
      if (!pendingLabel) pendingLabel = [];
      if (
        !pendingLabel.some(function (p) {
          return p.el === el;
        })
      )
        pendingLabel.push({ el: el, text: el.textContent });
      el.textContent = name;
    });
  }

  // restore: put the colour names back (the tap was cancelled); otherwise the swapped-in
  // section brings its own.
  function clearPending(main, restore) {
    if (main) main.removeAttribute("data-ic-pending");
    if (restore && pendingLabel) {
      pendingLabel.forEach(function (p) {
        if (p.el.isConnected) p.el.textContent = p.text;
      });
    }
    pendingLabel = null;
  }

  /* ---------- after the swap ---------- */

  function updateHead(data, push, variantId) {
    var url = new URL(data.url, location.origin);
    var params = new URLSearchParams(location.search);
    params.delete("variant");
    if (variantId) params.set("variant", variantId);
    var qs = params.toString();
    var next = url.pathname + (qs ? "?" + qs : "") + location.hash;
    if (push) history.pushState({ ic: data.handle }, "", next);
    else
      history.replaceState(
        Object.assign({}, history.state, { ic: data.handle }),
        "",
        next,
      );
    if (data.documentTitle) document.title = data.documentTitle;
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && data.absoluteUrl)
      canonical.setAttribute("href", data.absoluteUrl);
    var og = document.querySelector('meta[property="og:url"]');
    if (og && data.absoluteUrl) og.setAttribute("content", data.absoluteUrl);
    [
      ["og:title", data.title],
      ["og:description", data.description],
      ["og:image", data.imageUrl],
      ["og:image:secure_url", data.imageUrl],
      ["twitter:title", data.title],
      ["twitter:description", data.description],
      ["twitter:image", data.imageUrl],
    ].forEach(function (item) {
      var meta = document.querySelector(
        'meta[property="' + item[0] + '"], meta[name="' + item[0] + '"]',
      );
      if (meta && item[1] != null) meta.setAttribute("content", item[1]);
    });
    var description = document.querySelector('meta[name="description"]');
    if (description && data.description != null)
      description.setAttribute("content", data.description);
  }

  function track(data) {
    try {
      var k = data.klaviyo;
      if (k) {
        var q = (window._learnq = window._learnq || []);
        q.push(["track", "Viewed Product", k]);
        q.push([
          "trackViewedItem",
          {
            Title: k.Name,
            ItemId: k.ProductID,
            Categories: k.Categories,
            ImageUrl: k.ImageURL,
            Url: k.URL,
            Metadata: {
              Brand: k.Brand,
              Price: k.Price,
              CompareAtPrice: k.CompareAtPrice,
            },
          },
        ]);
      }
    } catch (e) {}
    try {
      if (
        window.Shopify &&
        Shopify.analytics &&
        typeof Shopify.analytics.publish === "function"
      ) {
        Shopify.analytics.publish("aetrex_colorway_viewed", {
          handle: data.handle,
          productId: data.id,
          variantId: data.variantId,
          url: data.url,
        });
      }
    } catch (e) {}
  }

  function afterSwapHooks(main) {
    try {
      if (
        window.AetrexTrueFitTile &&
        typeof window.AetrexTrueFitTile.refresh === "function"
      )
        window.AetrexTrueFitTile.refresh();
      var ph = main.querySelector(".tfc-fitrec-product");
      var block =
        ph && ph.closest('.shopify-app-block, [id^="shopify-block-"]');
      if (block && !block.querySelector("[data-tf-size-tile]")) {
        block.setAttribute("inert", "");
        block.setAttribute("aria-hidden", "true");
      }
    } catch (err) {}
  }

  // A status region inserted with the new section isn't announced, so a lasting one is used.
  function announce(data) {
    if (!data.color) return;
    var region = document.querySelector("[data-ic-live]");
    if (!region) {
      region = document.createElement("div");
      region.className = "sr-only";
      region.setAttribute("role", "status");
      region.setAttribute("aria-live", "polite");
      region.setAttribute("data-ic-live", "");
      document.body.appendChild(region);
    }
    region.textContent = "";
    setTimeout(function () {
      region.textContent = "Color: " + data.color;
    }, 60);
  }

  function refreshBelow(handle) {
    var myRun = ++belowRun;
    var ids = belowIds();
    ids.forEach(function (id) {
      var target = document.getElementById("shopify-section-" + id);
      if (target) target.setAttribute("aria-busy", "true");
    });
    getBelow(handle)
      .then(function (map) {
        if (myRun !== belowRun || !current || current.handle !== handle || !map)
          return;
        Object.keys(map).forEach(function (id) {
          var target = document.getElementById("shopify-section-" + id);
          if (!target || !map[id]) return;
          var tpl = document.createElement("template");
          tpl.innerHTML = map[id];
          var wrapper = tpl.content.querySelector(
            "#shopify-section-" + CSS.escape(id),
          );
          if (!wrapper) return;
          replaceSection(target, wrapper);
          target.dispatchEvent(
            new CustomEvent("shopify:section:load", {
              bubbles: true,
              detail: { sectionId: id },
            }),
          );
        });
        if (window.__yotpoLazyLoader) window.__yotpoLazyLoader.refresh();
      })
      .catch(noop)
      .then(function () {
        if (myRun !== belowRun) return;
        ids.forEach(function (id) {
          var target = document.getElementById("shopify-section-" + id);
          if (target) target.removeAttribute("aria-busy");
        });
      });
  }

  // A copy is used once: the next visit to that colour fetches it again, with current stock.
  function forget(handle) {
    var e = cache.get(handle);
    if (!e) return;
    cache.delete(handle);
    e.main = null;
    e.vmain = null;
    e.below = null;
  }

  function swapTo(e, opts) {
    var main = mainSection();
    if (!main) throw new Error("no section");
    var scrollPosition = opts.scrollPosition || {
      left: window.scrollX,
      top: window.scrollY,
      behavior: "instant",
    };
    var t0 = performance.now();
    var previous = current && current.handle;
    var hadFocus = main.contains(document.activeElement);

    clearPending(main, false);
    // A section request the theme still has in flight for the old colour would land on the
    // new one (it rewrites ?variant= in the address bar), so it is cancelled.
    var oldInfo = main.querySelector("product-info");
    try {
      if (oldInfo && oldInfo.abortController) oldInfo.abortController.abort();
    } catch (err) {}
    if (window.__aetrexStickyCleanup) window.__aetrexStickyCleanup();
    var selection = selectionState();
    if (selection && typeof selection.expect === "function")
      selection.expect(e.data && e.data.id, opts.wanted);
    replaceSection(main, e.wrapper);
    current = readData(main) || e.data;
    forget(current.handle);

    updateHead(current, opts.push, e.variantId || null);
    markActive(current.handle);
    // Picks first, so every listener of product:switched sees the final state at once.
    var told = carryPicks(main, opts.wanted, !!e.variantId);
    document.dispatchEvent(
      new CustomEvent("product:switched", {
        detail: {
          handle: current.handle,
          productId: current.id,
          source: "instant-colorway",
        },
      }),
    );
    afterSwapHooks(main);
    window.scrollTo(scrollPosition);
    announce(current);
    refocus = null;
    if (hadFocus || opts.focus) {
      var sw = Array.from(
        main.querySelectorAll(
          SWATCH + '[data-product-handle="' + CSS.escape(current.handle) + '"]',
        ),
      ).find(function (el) {
        return el.getClientRects().length > 0;
      });
      if (sw && sw.focus) {
        sw.focus({ preventScroll: true });
        keepFocus(sw, told);
      }
    }
    main.querySelectorAll(SWATCH).forEach(function (a) {
      a.setAttribute("data-no-instant", "");
    });

    var productJson = main.querySelector("script[data-product-json]");
    try {
      var product = JSON.parse(productJson.textContent);
      var want = {};
      (opts.wanted || []).forEach(function (w) {
        want[norm(w.name)] = norm(w.value);
      });
      var options = product.options.map(function (option) {
        return norm(typeof option === "string" ? option : option.name);
      });
      var allChosen = options.every(function (name) {
        return want[name] != null;
      });
      var variant =
        allChosen &&
        product.variants.find(function (v) {
          return (
            v.available &&
            options.every(function (name, i) {
              return norm(v.options[i]) === want[name];
            })
          );
        });
      current.variantId = variant ? variant.id : null;
    } catch (err) {
      current.variantId = null;
    }
    track(current);

    var t1 = performance.now();
    try {
      performance.measure("ic:swap", { start: opts.clickAt || t0, end: t1 });
    } catch (err) {}
    stats.push({
      handle: current.handle,
      cached: opts.cached,
      sized: !!e.variantId,
      wait: Math.round(t0 - (opts.clickAt || t0)),
      dom: Math.round(t1 - t0),
      total: Math.round(t1 - (opts.clickAt || t0)),
    });

    refreshBelow(current.handle);
    if (previous)
      setTimeout(function () {
        prefetch(previous, "low");
      }, 300);
    setTimeout(idlePrefetchAll, 1000);
  }

  function go(handle, opts) {
    opts = opts || {};
    var myToken = ++token;
    pendingHandle = handle;
    var clickAt = opts.clickAt || performance.now();
    var wanted = wantedNow(); // to pick a copy rendered for the size; re-read at swap time
    var e = cache.get(handle);
    var cached = !!(e && fresh(e.main) && e.wrapper);
    markActive(handle);
    var main = mainSection();
    if (main) {
      main.setAttribute("aria-busy", "true");
      showPending(main, handle);
    }

    var fallback = setTimeout(function () {
      if (myToken !== token) return;
      ++token; // the page is navigating: a copy landing now must not swap or push history
      location.assign(navigationPath(handle));
    }, SWAP_WAIT_MS);

    getMain(handle, "high")
      .then(function (plain) {
        return readyVariant(plain, variantIdFor(plain, wanted)).then(
          function (sized) {
            return { plain: plain, ready: sized || plain };
          },
        );
      })
      .then(function (r) {
        var warm = warmHero(r.ready, true);
        if (r.ready.heroReady) return r;
        return Promise.race([warm, delay(HERO_WAIT_MS)]).then(function () {
          return r;
        });
      })
      .then(function (r) {
        if (myToken !== token) return;
        clearTimeout(fallback);
        pendingHandle = null;
        // The shopper may have picked another size while this was loading: carry what is
        // picked now, and drop a copy rendered for the old size.
        var latest = wantedNow();
        var use = r.ready;
        if (use.variantId && variantIdFor(r.plain, latest) !== use.variantId)
          use = r.plain;
        swapTo(use, {
          push: opts.push !== false,
          clickAt: clickAt,
          cached: cached,
          focus: opts.focus,
          wanted: latest,
          scrollPosition: opts.scrollPosition,
        });
      })
      .catch(function () {
        clearTimeout(fallback);
        if (myToken === token) location.assign(navigationPath(handle));
      })
      .then(function () {
        if (myToken !== token) return;
        var m = mainSection();
        if (m) m.removeAttribute("aria-busy");
      });
  }

  // The shopper went back to the colour on screen before the swap landed.
  function cancelPending(handle) {
    if (pendingHandle) ++token;
    pendingHandle = null;
    markActive(handle);
    var main = mainSection();
    if (main) main.removeAttribute("aria-busy");
    clearPending(main, true);
  }

  function onIntent(ev) {
    var sw = ev.target && ev.target.closest && ev.target.closest(SWATCH);
    if (!sw) return;
    var main = mainSection();
    if (!main || !main.contains(sw)) return;
    prefetch(sw.getAttribute("data-product-handle"), "high");
  }

  function onClick(ev) {
    if (ev.defaultPrevented) return;
    var sw = ev.target && ev.target.closest && ev.target.closest(SWATCH);
    if (!sw) return;
    var main = mainSection();
    if (!main || !main.contains(sw)) return;
    if (
      ev.button !== 0 ||
      ev.metaKey ||
      ev.ctrlKey ||
      ev.shiftKey ||
      ev.altKey
    ) {
      // New tab / window: the swatch is a link and opens normally; the theme's own in-place
      // switch must not also run here.
      ev.stopImmediatePropagation();
      return;
    }
    var handle = sw.getAttribute("data-product-handle");
    ev.preventDefault();
    ev.stopImmediatePropagation();
    if (!handle) return;
    if (current && handle === current.handle) {
      cancelPending(handle);
      return;
    }
    go(handle, {
      clickAt: ev.timeStamp || performance.now(),
      focus: ev.detail === 0,
      scrollPosition: {
        left: window.scrollX,
        top: window.scrollY,
        behavior: "instant",
      },
    });
  }

  function onPopState() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = m && decodeURIComponent(m[1]);
    if (!handle) return;
    if (current && handle === current.handle) {
      cancelPending(handle);
      return;
    }
    var main = mainSection();
    var known =
      main &&
      main.querySelector(
        SWATCH + '[data-product-handle="' + CSS.escape(handle) + '"]',
      );
    if (known) go(handle, { push: false });
    else location.reload();
  }

  function captureChoices() {
    var main = mainSection();
    chosenOptions = {};
    if (!main) return;
    main.querySelectorAll(OPTION_INPUT + ":checked").forEach(function (input) {
      if (input.name && !input.disabled)
        chosenOptions[input.name] = input.value;
    });
  }

  function trackChoices(ev) {
    var main = mainSection();
    if (
      !main ||
      !main.contains(ev.target) ||
      !ev.target.matches ||
      !ev.target.matches(OPTION_INPUT)
    )
      return;
    captureChoices();
    if (!carrying) queueVariantPrefetch();
  }

  function navigationPath(handle) {
    var url = new URL(productPath(handle), location.origin);
    var params = new URLSearchParams(location.search);
    params.delete("variant");
    url.search = params.toString();
    url.hash = location.hash;
    return url.href;
  }

  function report() {
    var totals = stats
      .map(function (s) {
        return s.total;
      })
      .sort(function (a, b) {
        return a - b;
      });
    var at = function (q) {
      return totals.length
        ? totals[Math.min(totals.length - 1, Math.floor(q * totals.length))]
        : null;
    };
    return {
      swaps: stats.length,
      cached: stats.filter(function (s) {
        return s.cached;
      }).length,
      sized: stats.filter(function (s) {
        return s.sized;
      }).length,
      p50: at(0.5),
      p90: at(0.9),
      max: totals.length ? totals[totals.length - 1] : null,
      prefetched: Array.from(cache.keys()).filter(function (h) {
        return fresh(cache.get(h).main);
      }),
    };
  }

  function init() {
    var main = mainSection();
    current = readData(main);
    if (!main || !current) return;
    injectStyles();
    captureChoices();

    main.querySelectorAll(SWATCH).forEach(function (a) {
      a.setAttribute("data-no-instant", "");
    });
    history.replaceState(
      Object.assign({}, history.state, { ic: current.handle }),
      "",
    );

    window.addEventListener("click", onClick, true);
    document.addEventListener("pointerover", onIntent, { passive: true });
    document.addEventListener("touchstart", onIntent, {
      passive: true,
      capture: true,
    });
    document.addEventListener("focusin", onIntent);
    document.addEventListener("change", trackChoices, true);
    window.addEventListener("popstate", onPopState);
    document.addEventListener("focusin", onFocusIn, true);
    window.__instantColorway.active = true;

    var start = function () {
      setTimeout(idlePrefetchAll, 1500);
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
  }

  window.__instantColorway = {
    go: go,
    cache: cache,
    stats: stats,
    prefetch: prefetch,
    report: report,
  };

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
